import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Play } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from '../ui/dialog';
import AgentReasoningDemo from '../AgentReasoningDemo';

const AgentTitleSlide = ({ name, role, image, subtitle, theme = 'light', demoScenario }) => {
  const isDark = theme === 'dark';
  const [demoOpen, setDemoOpen] = useState(false);

  return (
    <div className={`h-full w-full flex items-center justify-center p-12 relative overflow-hidden ${isDark ? 'bg-slate-950 text-white' : 'bg-slate-50 text-slate-900'}`}>
        {/* Background Elements */}
        {isDark ? (
            <>
                <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-blue-900/20 rounded-full blur-[120px] -translate-y-1/2 translate-x-1/2" />
                <div className="absolute bottom-0 left-0 w-[800px] h-[800px] bg-indigo-900/20 rounded-full blur-[120px] translate-y-1/2 -translate-x-1/2" />
            </>
        ) : (
            <>
                <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-blue-100/60 rounded-full blur-[120px] -translate-y-1/2 translate-x-1/2" />
                <div className="absolute bottom-0 left-0 w-[800px] h-[800px] bg-purple-100/60 rounded-full blur-[120px] translate-y-1/2 -translate-x-1/2" />
            </>
        )}

        <div className="relative z-10 flex flex-col items-center text-center max-w-4xl mx-auto space-y-12">

            {/* Animated Image Container */}
            <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                whileInView={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.8, ease: "easeOut" }}
                className="relative"
            >
                {/* Rotating Gradient Border */}
                <div className="absolute -inset-4 rounded-full bg-gradient-to-r from-sky-400 via-blue-500 to-purple-500 opacity-75 blur-md animate-spin-slow"
                     style={{ animationDuration: '8s' }} />

                {/* Image */}
                <div className={`relative w-48 h-48 md:w-64 md:h-64 rounded-full overflow-hidden border-8 ${isDark ? 'border-slate-900' : 'border-white'} shadow-2xl`}>
                    <img src={image} alt={name} className="w-full h-full object-cover" />
                </div>

                {/* Status Indicator */}
                <div className={`absolute bottom-4 right-4 w-8 h-8 ${isDark ? 'bg-green-500 border-slate-900' : 'bg-green-500 border-white'} border-4 rounded-full z-20`} />
            </motion.div>

            {/* Text Content */}
            <div className="space-y-6">
                <motion.h1
                    initial={{ y: 20, opacity: 0 }}
                    whileInView={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.3, duration: 0.6 }}
                    className="text-5xl md:text-7xl font-bold tracking-tight"
                >
                    {name}, <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-500 to-purple-600">{role}</span>
                </motion.h1>

                <motion.p
                    initial={{ y: 20, opacity: 0 }}
                    whileInView={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.5, duration: 0.6 }}
                    className={`text-xl md:text-3xl ${isDark ? 'text-slate-400' : 'text-slate-500'} font-light max-w-2xl mx-auto leading-relaxed`}
                >
                    {subtitle}
                </motion.p>

                {/* View Demo Button */}
                {demoScenario && (
                    <motion.div
                        initial={{ y: 20, opacity: 0 }}
                        whileInView={{ y: 0, opacity: 1 }}
                        transition={{ delay: 0.7, duration: 0.6 }}
                    >
                        <button
                            onClick={() => setDemoOpen(true)}
                            className="inline-flex items-center gap-2.5 px-6 py-3 rounded-full text-sm font-semibold transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105 bg-gradient-to-r from-blue-600 to-blue-500 text-white hover:from-blue-500 hover:to-blue-400"
                        >
                            <Play className="w-4 h-4 fill-current" />
                            View Reasoning Demo
                        </button>
                    </motion.div>
                )}
            </div>
        </div>

        {/* Demo Modal */}
        {demoScenario && (
            <Dialog open={demoOpen} onOpenChange={setDemoOpen}>
                <DialogContent
                    className="max-w-[95vw] w-[95vw] h-[90vh] max-h-[90vh] bg-white border-0 shadow-2xl rounded-2xl p-0 overflow-hidden gap-0"
                    style={{ display: 'flex', flexDirection: 'column' }}
                >
                    <DialogTitle className="sr-only">{name} Reasoning Demo</DialogTitle>
                    <div className="flex-1 min-h-0 p-6">
                        {/* key forces remount so animation restarts every open */}
                        {demoOpen && <AgentReasoningDemo key={Date.now()} scenario={demoScenario} />}
                    </div>
                </DialogContent>
            </Dialog>
        )}
    </div>
  );
};

export default AgentTitleSlide;
